import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { closeModal, Modal, SpinningLoader } from "risa-oasis-ui_v2";
import {
  downloadAuditTrail,
  DownloadAuditTrailPayload,
} from "../../../api/postCall/downloadAuditTrail";
import { FinalWorklistDataResponse } from "../../../data-model/finalWorklistModel";
import { UserGeneralInfoContextKeys } from "../../../enums/userGeneralInfoContextKeys";
import { RootState } from "../../../redux/store/store";
import { UserStore } from "../../../store/UserGeneralInfoStore";
import { logError } from "../../../utils/customLogger";
import PdfRender from "../../pdfRender/pdfRender";
import RefreshButton from "../../refreshButton/refreshButton";
import { ToastMessages } from "../../toasts/toastMessages";

interface EmbedPDFModalProps {
  id: string;
  isModalOpen: boolean;
  onClose: () => void;
  rowData?: FinalWorklistDataResponse;
  uploadId?: string;
}

const EmbedPDFModal = (props: EmbedPDFModalProps) => {
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const { data: finalWorklistData } = useSelector(
    (state: RootState) => state.finalWorklistOrdersData,
  );

  useEffect(() => {
    setPdfUrl("");
    setError("");
    setIsLoading(false);
  }, [props.id]);

  useEffect(() => {
    fetchPDF();
  }, [props.id]);

  const handleClose = () => {
    if (props.onClose) {
      props.onClose();
    }
    closeModal(`embed-pdf-modal`);
  };

  const getOrgIdForFetch = () => {
    const internalOrgId = UserStore.getItem(
      UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
    );
    const healthcareFacilityId = UserStore.getItem(
      UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID,
    );

    return internalOrgId || healthcareFacilityId || "";
  };

  const fetchPDF = async () => {
    try {
      setIsLoading(true);
      setError("");

      if (!props.id) {
        setError("No order ID provided");
        setIsLoading(false);
        return;
      }

      // Fallback to original Firebase approach if no row data found
      const orgId = getOrgIdForFetch();
      const filePath = `medical_pa/${orgId}/oncoemr_audit_trail/${props.uploadId}.pdf`;

      const { getFileDownloadUrl } =
        await import("../../../api/firebase/firestoreService");
      const downloadUrl = await getFileDownloadUrl(filePath);

      if (downloadUrl) {
        setPdfUrl(downloadUrl);
      } else {
        setError(`PDF not found for this order`);
      }
    } catch (err) {
      logError(err as Error, "Error fetching PDF for order");
      setError("Failed to load PDF. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    try {
      const rowData = finalWorklistData?.find(
        (item) => item.upload_id === props.id,
      );

      if (rowData) {
        const orgId = getOrgIdForFetch();

        const payload: DownloadAuditTrailPayload = {
          identifier: rowData.upload_id,
          patient_mrn: rowData.mrn,
          visit_date: rowData.date_of_service,
          drug_name: rowData.regimen,
          org_id: orgId,
        };

        downloadAuditTrail(payload);
        toast.success(ToastMessages.REFRESH_PDF_SUCCESS);
        handleClose();
      }
    } catch (err) {
      logError(err as Error, "Error refreshing PDF for order");
      setError("Failed to refresh PDF. Please try again later.");
      toast.error(ToastMessages.REFRESH_PDF_FAILURE);
    }
  };
  return (
    <Modal
      dialogId={`embed-pdf-modal`}
      onSave={handleClose}
      onCancel={handleClose}
      onClose={handleClose}
      title={`Audit Trail`}
      saveButtonText={"Close"}
      cancelText={"Cancel"}
      disableSave={false}
      heightPercentage={80}
      showSingleButton={true}
    >
      <div className="embed-pdf-modal__container h-full w-full">
        <div className="mb-2 flex justify-end">
          <RefreshButton onClick={handleRefresh} />
        </div>
        {isLoading && (
          <div className="flex h-full w-full items-center justify-center gap-2">
            <SpinningLoader />
            <span>Loading PDF...</span>
          </div>
        )}

        {error && (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-center text-sm text-red-500">{error}</div>
          </div>
        )}

        {!isLoading && !error && pdfUrl && (
          <div className="h-full w-full overflow-hidden">
            <PdfRender file={pdfUrl} hideRotateButtons={true} />
          </div>
        )}

        {!isLoading && !error && !pdfUrl && (
          <div className="flex h-full w-full items-center justify-center">
            <div className="text-center text-sm text-gray-500">
              No PDF available for this order
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EmbedPDFModal;
